import { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from 'urql';

import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { SearchProductCardVariationDataDocument } from '@packages/gql/generated/shopping/SearchProductCardVariationDataDocument';

/* GraphQL */ `
  query SearchProductCardVariationData($resultId: ID!, $styleId: ID!) {
    styleDetails(resultId: $resultId, styleId: $styleId) {
      variationGroups {
        ...SearchProductCardFullVariationGroupFragment
        ...SearchProductCardClickTrackingVariationGroupFragment
        ...SearchProductCardGlycerinTrackingVariationGroupFragment
      }
    }
  }
`;

const messages = defineMessages({
  notification: {
    id: 'colorSelectFailed.notification',
    defaultMessage:
      'Beim Laden der ausgewählten Farbe ist ein Fehler aufgetreten. Bitte versuche es erneut.',
  },
});

export const useDeferredVariationGroup = ({
  resultId,
  styleId,
}: {
  resultId: string;
  styleId: string;
}) => {
  // local state to allow changing colors, also used for fetching full product data
  const [activeVariationIndex, setActiveVariationIndex] = useState(0);
  // ref to check if data has already failed to fetch
  // because if there is an error the useQuery won't fetch data again
  // unless the query is reexecuted with { requestPolicy: 'network-only' }
  const hasErrorBeenHandled = useRef<boolean>(false);

  const [{ data, error, fetching }, executeQuery] = useQuery({
    query: SearchProductCardVariationDataDocument,
    variables: {
      resultId,
      styleId,
    },
    pause: activeVariationIndex === 0,
    context: useMemo(() => ({ suspense: false }), []),
  });

  const { addNotification } = useNotification();

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (error) {
      addNotification({
        severity: 'error',
        message: formatMessage(messages.notification),
      });
    }
  }, [error, addNotification, formatMessage]);

  // if failed to fetch full variation data,
  // fall back to the first variation and show a error notification
  useEffect(() => {
    // if there is no error, the active variation is already 0,
    // or query is still fetching, do early return
    if (!error || activeVariationIndex === 0 || fetching) return;

    // if error is already occured in a prior fetch, re-execute the query without using cache
    // and set the error flag to default value
    if (hasErrorBeenHandled.current && !fetching) {
      hasErrorBeenHandled.current = false;
      executeQuery({ requestPolicy: 'network-only' });
      return;
    }

    // if error is not already handled in a prior fetch, set the error flag to true,
    // reset active variation index to 0 and show the error notification
    hasErrorBeenHandled.current = true;
    setActiveVariationIndex(0);
    addNotification({
      severity: 'warning',
      message: formatMessage(messages.notification),
      duration: 7000,
    });
  }, [activeVariationIndex, addNotification, error, executeQuery, fetching, formatMessage]);

  // if full variation data is not yet available, fall back to the first variation until data has loaded
  return {
    maskedDeferredVariationGroup: data?.styleDetails.variationGroups[activeVariationIndex],
    activeVariationIndex,
    setActiveVariationIndex,
    isActiveVariationLoading: fetching,
  };
};
